import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Error } from 'components/Common/Error';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { MenuItem } from '../../../../../../../../components/styled/SelectMenuItem';
import ArrowDownIcon from '../../../../../../../../components/SvgIcons/ArrowDownIcon';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { TriOptionsModel } from '../../../../../../../FrontDeskModule/Shared/components/FrontDeskToolbar';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { CompanyField } from '../../../../../../components/Inputs/CompanyField';
import { NationalityFieldMultiSelect } from '../../../../../../components/Inputs/NationalityFieldMultiSelect';
import { RateField } from '../../../../../../components/Inputs/RateField';
import { RoomTypeField } from '../../../../../../components/Inputs/RoomTypeField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { ArrivalReportFormModel } from '../../models';
import { StateField } from './fields/StateField';

const OrderByItems = [
  { value: TriOptionsModel.first_name, label: TriOptionsModel.first_name },
  { value: TriOptionsModel.last_name, label: TriOptionsModel.last_name },
  { value: TriOptionsModel.agency, label: TriOptionsModel.agency },
  { value: TriOptionsModel.crs_number, label: TriOptionsModel.crs_number },
  { value: TriOptionsModel.confirmation_number, label: TriOptionsModel.confirmation_number },
  { value: TriOptionsModel.room_number, label: TriOptionsModel.room_number },
  { value: TriOptionsModel.room_type, label: TriOptionsModel.room_type },
  { value: TriOptionsModel.arrival_date, label: TriOptionsModel.arrival_date },
  { value: TriOptionsModel.departure_date, label: TriOptionsModel.departure_date },
  { value: TriOptionsModel.arrival_time, label: TriOptionsModel.arrival_time },
];

export const FilterForm: FC<{
  onSubmit: (data: ArrivalReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const { t } = useTranslation('frontDesk');
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ArrivalReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Arrival Report" />
        <Stack direction={'column'} spacing={2} p={3}>
          <DateRangePicker
            value={dates}
            onChange={handleChangeDates}
            renderInput={({ startProps, endProps }) => (
              <Stack
                direction="row"
                spacing="1rem"
                sx={{
                  width: '100%',
                }}
              >
                <TextField
                  {...startProps}
                  size="medium"
                  variant="standard"
                  sx={{
                    width: '50%',
                  }}
                  label="From"
                  error={Boolean(errors?.from)}
                  helperText={errors?.from?.message}
                  placeholder="mm/dd/yyyy"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Calendar2Icon
                          sx={{
                            fontSize: '1rem',
                            color: ({ palette }) => palette.common.black,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  {...endProps}
                  size="medium"
                  variant="standard"
                  sx={{
                    width: '50%',
                  }}
                  label="To"
                  error={Boolean(errors?.to)}
                  helperText={errors?.to?.message}
                  placeholder="mm/dd/yyyy"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Calendar2Icon
                          sx={{
                            fontSize: '1rem',
                            color: ({ palette }) => palette.common.black,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            )}
          />
          <Controller
            control={control}
            name="card_ids"
            render={({ field }) => (
              <CompanyField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />
          <Controller
            control={control}
            name="rate_ids"
            render={({ field, fieldState, formState }) => (
              <RateField
                value={field.value}
                onChange={value => field.onChange(value)}
                error={
                  formState?.errors[field.name] ? (
                    <ErrorMessage
                      errors={formState.errors}
                      name={field.name}
                      render={({ message }) => <Error>{message}</Error>}
                    />
                  ) : null
                }
              />
            )}
          />
          <Controller
            control={control}
            name="room_type_ids"
            render={({ field, fieldState, formState }) => (
              <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />

          <Stack direction={'row'} spacing={1}>
            <Box sx={{ flexGrow: 1 }}>
              <Controller
                control={control}
                name="sortBy"
                render={({ field, fieldState, formState }) => (
                  <FormControl variant={'standard'} fullWidth>
                    <InputLabel id="select-multiple">Order By</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={field.value}
                      label="Age"
                      onChange={field.onChange}
                    >
                      {OrderByItems?.map((item, index) => {
                        return (
                          <MenuItem value={item?.value} key={index}>
                            {t(`tri.${item.label}`)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box sx={{ width: 90, pt: 2 }}>
              <Controller
                control={control}
                name="sortDirection"
                render={({ field, fieldState, formState }) => (
                  <Button
                    fullWidth
                    onClick={() => field.onChange(field.value === 'asc' ? 'desc' : 'asc')}
                    sx={{
                      height: 48,
                      backgroundColor: '#fff',
                      boxShadow: 'inset 0 -1.5px 0 0 #b7beca',
                    }}
                  >
                    <Typography variant="inter14" color="colors.purple.o70">
                      {field.value === 'asc' ? 'ASC' : 'DESC'}
                    </Typography>
                    <ArrowDownIcon
                      fontSize="small"
                      sx={{
                        ml: 1,
                        ...(field.value === 'asc' ? { transform: 'rotate(180deg)' } : {}),
                      }}
                    />
                  </Button>
                )}
              />
            </Box>
          </Stack>

          <Controller
            control={control}
            name="grouped"
            render={({ field }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                {...field}
                checked={field.value ?? false}
                onChange={(e, checked) => field.onChange(checked)}
                label={
                  <Typography variant="inter14" fontWeight={500} sx={{ ml: '1rem' }}>
                    Grouped By Market/Agency
                  </Typography>
                }
                control={<Switch />}
              />
            )}
          />

          <Divider />
          <Controller
            control={control}
            name="states"
            render={({ field, fieldState, formState }) => (
              <StateField value={field.value} onChange={value => field.onChange(value)} />
            )}
          />
          <Controller
            control={control}
            name="nationality_ids"
            render={({ field, fieldState, formState }) => (
              <NationalityFieldMultiSelect
                value={field.value}
                onChange={value => field.onChange(value)}
              />
            )}
          />
          <Box sx={{ height: 48, display: 'flex', alignItems: 'center', mt: 1 }}>
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Price
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
          </Box>

          <Box sx={{ height: 48, display: 'flex', alignItems: 'center', mt: 1 }}>
            <Controller
              control={control}
              name="exclude_partial"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Exclude partial
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />
          </Box>
        </Stack>
        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
